import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import Page from 'views/Layout/Pages/Page';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import * as consts from '../../consts/consts';
import { type SharedCustomSurveyReportItemData } from '../Administration/CustomSurveys/consts';

// Marks a report opened from this list, so the standalone report page can
// offer a way back to it. Links opened from the survey-admin builder skip
// this marker, since there's nothing to go back to from there.
export const REPORT_ORIGIN_PARAM = 'from';
export const REPORT_ORIGIN_LIFECYCLE_SURVEYS = 'lifecycle-surveys';

// "Lifecycle surveys" under Team: every published report item a person has
// been shared with, with a link to view each one. Empty for most people -
// nothing has been shared with them yet - so it reads as an empty state
// rather than an error.
const SharedSurveyReportsPage: FC = () => {
  const { formatMessage } = useIntl();

  const { data: items, status } = useConfirmApi<
    SharedCustomSurveyReportItemData[]
  >({
    method: 'GET',
    url: 'custom-survey/report-items/shared-with-me',
  });

  const itemPath = useCallback(
    (itemId: number): string =>
      `${consts
        .CUSTOM_SURVEY_REPORT_ITEM(formatMessage)
        .path.replace(
          ':itemId',
          String(itemId)
        )}?${REPORT_ORIGIN_PARAM}=${REPORT_ORIGIN_LIFECYCLE_SURVEYS}`,
    [formatMessage]
  );

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.custom_surveys.shared_reports.column.survey',
          defaultMessage: 'Survey',
        }),
        field: 'survey',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.custom_surveys.shared_reports.column.report',
          defaultMessage: 'Report',
        }),
        field: 'report',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.custom_surveys.shared_reports.column.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        sortable: false,
        className: 'col-auto text-nowrap',
      },
    ],
    [formatMessage]
  );

  const rows = useMemo(
    () =>
      (items ?? []).map((item) => ({
        id: item.id,
        survey: item.survey_name,
        report: (
          <span>
            <span className="d-block">{item.report_name}</span>
            {/* The audience this copy of the report covers, e.g. a leader's org. */}
            {item.name && <span className="text-muted small">{item.name}</span>}
          </span>
        ),
        actions: (
          <Button
            color="link"
            className="p-0"
            tag={Link}
            to={itemPath(item.id)}
          >
            <FormattedMessage
              id="app.views.custom_surveys.shared_reports.view"
              defaultMessage="View report"
            />
          </Button>
        ),
      })),
    [items, itemPath]
  );

  const title = formatMessage({
    id: 'app.consts.TEAM_LIFECYCLE_SURVEYS.name',
    defaultMessage: 'Lifecycle surveys',
  });

  return (
    <Page title={title}>
      <div className="d-flex flex-column gap-4 mt-4">
        {status === 'LOADING' && <Loading />}

        {status === 'ERROR' && (
          <Card>
            <CardBody>
              <EmptyState
                title={formatMessage({
                  id: 'app.views.custom_surveys.shared_reports.load_error',
                  defaultMessage: 'Could not load your reports.',
                })}
              />
            </CardBody>
          </Card>
        )}

        {status === 'SUCCESS' &&
          (rows.length === 0 ? (
            <Card>
              <CardBody>
                <EmptyState
                  title={formatMessage({
                    id: 'app.views.custom_surveys.shared_reports.empty.title',
                    defaultMessage: 'No reports shared with you',
                  })}
                  subtitle={formatMessage({
                    id: 'app.views.custom_surveys.shared_reports.empty.subtitle',
                    defaultMessage:
                      'Survey reports shared with you will show up here.',
                  })}
                />
              </CardBody>
            </Card>
          ) : (
            <FilterablePeopleTable
              rows={rows}
              columns={columns}
              hideFilters={true}
              hideExportButton={true}
            />
          ))}
      </div>
    </Page>
  );
};

export default SharedSurveyReportsPage;
